/**
 * PostHog Provider — initializes PostHog on China/global web and app WebView.
 */
import { useEffect, useRef, useState, createContext, useContext, ReactNode } from "react";
import config from "@Config/index";
import useIsApp, { useIsNewNativeApp } from "hooks/useIsApp";
import { subscribeToWebViewEvent } from "@Utils/webviewEvents";
import { WEB_VIEW_APP_EVENTS } from "@Utils/webApputils/analyticEvents";
import { getPostHogConfig, isPostHogEnabled } from "./posthogConfig";
import {
  posthogIdentify,
  registerPostHogSuperProperties,
  setPostHogInstance,
} from "./posthogEventEmitter";

type PostHogInstance = any;

interface PostHogContextValue {
  posthog: PostHogInstance | null;
  isInitialized: boolean;
}

const PostHogContext = createContext<PostHogContextValue>({
  posthog: null,
  isInitialized: false,
});

interface PostHogProviderProps {
  children: ReactNode;
}

export const PostHogProvider = ({ children }: PostHogProviderProps) => {
  const [posthog, setPosthog] = useState<PostHogInstance | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const isAmberApp = useIsApp();
  const isNativeApp = useIsNewNativeApp();
  const initStartedRef = useRef(false);

  useEffect(() => {
    if (!isPostHogEnabled() || initStartedRef.current) {
      return undefined;
    }

    initStartedRef.current = true;
    const posthogConfig = getPostHogConfig();
    let unsubscribeDistinctId: (() => void) | undefined;

    const initPostHog = async () => {
      try {
        // eslint-disable-next-line import/no-extraneous-dependencies
        const posthogModule = await import("posthog-js");
        const posthogInstance = posthogModule.default;

        // Read the native distinct_id injected by JSBootstrap (native → web at document start)
        const nativeDistinctId = (window as any).posthogDistinctId;

        posthogInstance.init(posthogConfig.apiKey, {
          api_host: posthogConfig.host,
          autocapture: false,
          capture_pageview: true,
          capture_pageleave: true,
          capture_performance: false,
          capture_dead_clicks: false,
          rageclick: false,
          disable_session_recording: true,
          disable_surveys: true,
          enable_heatmaps: false,
          // Bootstrap with native distinct_id when running inside the app WebView
          ...(isNativeApp && nativeDistinctId
            ? { bootstrap: { distinctID: nativeDistinctId } }
            : {}),
          loaded: (ph) => {
            setPostHogInstance(ph);
            registerPostHogSuperProperties().catch(() => undefined);

            let lastAttributedFcmToken: string | null = null;

            const attributeFcmToken = (token: string) => {
              if (token && token !== lastAttributedFcmToken) {
                lastAttributedFcmToken = token;
                ph.setPersonProperties({ fcm_token: token });
              }
            };

            const existingToken = (window as any).fcmToken;
            if (existingToken) {
              attributeFcmToken(existingToken);
            } else {
              let _fcmToken: string | null = null;
              Object.defineProperty(window, "fcmToken", {
                get: () => _fcmToken,
                set: (value: string) => {
                  _fcmToken = value;
                  attributeFcmToken(value);
                },
                configurable: true,
                enumerable: true,
              });
            }

            if (!config.isProduction) {
              // eslint-disable-next-line testing-library/no-debug
              ph.debug();
            }
          },
        });

        setPostHogInstance(posthogInstance);
        setPosthog(posthogInstance);
        setIsInitialized(true);

        // Subscribe to native-to-web POSTHOG_DISTINCT_ID events (post-reset sync)
        if (isNativeApp) {
          unsubscribeDistinctId = subscribeToWebViewEvent(
            WEB_VIEW_APP_EVENTS.POSTHOG_DISTINCT_ID,
            (payload) => {
              if (payload?.distinctId && posthogInstance) {
                // Through `posthogIdentify`, not `identify` directly:
                // identifying moves this browser onto a different person, and a
                // bare identify gives that person no properties — dropping the
                // `user_id` and attribution every other identify carries.
                posthogIdentify(payload.distinctId).catch(() => undefined);
              }
            },
          );
        }

        import("./posthogTestEvents");
      } catch (error) {
        console.error("Failed to initialize PostHog:", error);
        initStartedRef.current = false;
      }
    };

    initPostHog();

    return () => {
      if (unsubscribeDistinctId) {
        unsubscribeDistinctId();
      }
    };
  }, [isAmberApp, isNativeApp]);

  return (
    <PostHogContext.Provider value={{ posthog, isInitialized }}>{children}</PostHogContext.Provider>
  );
};

export const usePostHog = (): PostHogInstance | null => {
  const { posthog } = useContext(PostHogContext);
  return posthog;
};

export const usePostHogInitialized = (): boolean => {
  const { isInitialized } = useContext(PostHogContext);
  return isInitialized;
};

export default PostHogProvider;
